import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import * as Parse from 'parse';
import { OrganizationService } from './organization.service';

export enum CONTRACT_TYPE {
    EMAIL = 'email',
    ESIGN = 'esign'
}

export enum CONTRACT_STATUS {
    DRAFT = 'draft',
    SENDING = 'sending', // Used to trigger server into sending the contract
    SENT = 'sent',
    SIGNED = 'signed'
}

export interface IContractForm {
    firstname: string;
    lastname: string;
    email: string;
    subject: string;
    body: string;
    esign: boolean;
}

@Injectable()
export class ContractService {

    constructor(private auth: AuthService, private orgService: OrganizationService) {
    }

    async createContract(
        name: string,
        dataSources: any[],
        template: Parse.Object,
        templatePDF: File,
        property: Parse.Object
    ): Promise<Parse.Object> {
        const contract: Parse.Object = new Parse.Object('Contract');

        if (template) {
            contract.set('dataSources', dataSources);
            contract.set('template', template);
            contract.set('contractType', template.get('type'));
        } else if (templatePDF) {
            const pdf = new Parse.File(Math.random().toString(16), templatePDF);
            await pdf.save();
            contract.set('templatePDF', pdf);
            contract.set('templatePDFName', templatePDF.name);
            contract.set('contractType', 'manual-pdf');
        }

        contract.set('name', name);
        contract.set('property', property);
        contract.set('status', CONTRACT_STATUS.DRAFT);
        contract.set('creator', this.auth.currentUser);
        contract.set('organization', this.orgService.organization);

        if (this.auth.userType === 'AAA') {
            contract.set('creator', this.auth.currentUser.get('assistantOf'));
        }

        return contract.save(null, { cascadeSave: false });
    }

    fetchDirectContracts(): Promise<Parse.Object[]> {
        const query: Parse.Query = new Parse.Query('Contract');

        query.equalTo('organization', this.orgService.organization);
        query.containedIn('status', [CONTRACT_STATUS.SENT, CONTRACT_STATUS.SIGNED]);
        query.equalTo('isDirectContract', true);
        query.descending('createdAt');
        query.include('creator');

        if (this.auth.userType !== 'Admin') {
            query.equalTo('creator', this.auth.currentUser);
        }

        return query.find();
    }

    fetchContracts(status: 'draft' | 'non-draft', property: Parse.Object): Promise<Parse.Object[]> {
        const query: Parse.Query = new Parse.Query('Contract');
        query.equalTo('organization', this.orgService.organization);

        if (status === 'draft') {
            if (this.auth.userType === 'AAA') {
                query.containedIn('creator', [this.auth.currentUser, this.auth.currentUser.get('assistantOf')]);
            } else {
                query.equalTo('creator', this.auth.currentUser);
            }

            query.equalTo('status', CONTRACT_STATUS.DRAFT);
            query.equalTo('property', property);
            query.descending('createdAt');
        } else if (status === 'non-draft') {
            query.notEqualTo('status', CONTRACT_STATUS.DRAFT);
            query.equalTo('property', property);
            query.descending('createdAt');
            query.include('creator');

            // Visiblity logic
            switch (this.auth.userType) {
                case 'Admin':
                    // Sees all contracts
                    break;
                case 'AAA':
                    query.containedIn('creator', [this.auth.currentUser, this.auth.currentUser.get('assistantOf')]);
                    break;
                case 'TC':
                case 'AA':
                case 'SA':
                    query.equalTo('creator', this.auth.currentUser);
                    break;
                default:
                    break;
            }
        }

        return query.find();
    }

    deleteContract(contract: Parse.Object): Promise<Parse.Object> {
        return contract.destroy();
    }

    fetchContractById(id: string) {
        const contract: Parse.Object = new Parse.Object('Contract');
        contract.id = id;

        return contract.fetch();
    }

    async uploadAddendums(addendums: File[]): Promise<Parse.File[]> {
        const promises = [];

        for (const addendum of addendums) {
            promises.push(new Parse.File(Math.random().toString(16), addendum, 'application/pdf').save());
        }

        return Promise.all(promises);
    }

    async saveEmailContract(contract: Parse.Object, values: IContractForm) {
        contract.set('type', CONTRACT_TYPE.EMAIL);
        contract.set('destination', [{ email: values.email, name: values.firstname + ' ' + values.lastname }]);
        contract.set('emailSubject', values.subject);
        contract.set('emailBody', values.body);
        contract.set('status', CONTRACT_STATUS.SENDING); // Triggers hook on server to send the email

        return contract.save(null, { cascadeSave: false });
    }

    async saveEsignContract(contract: Parse.Object, values: IContractForm) {
        contract.set('type', CONTRACT_TYPE.ESIGN);
        contract.set('destination', [{ email: values.email, name: values.firstname + ' ' + values.lastname }]);
        contract.set('emailSubject', values.subject);
        contract.set('emailBody', values.body);

        return contract.save(null, { cascadeSave: false });
    }

    /**
     * Renders the final contract PDF and returns the job result
     */
    renderContract(contract: Parse.Object, waitForRender = true): Promise<any> {
        return new Promise(async (resolve, reject) => {
            try {
                await Parse.Cloud.run('startContractRender', {contractId: contract.id});

                if (!waitForRender) {
                    return resolve(contract);
                } else {
                    const timer = setInterval(async () => {
                        try {
                            const result = await Parse.Cloud.run('getJobStatusForContract', {contractId: contract.id});

                            if (result?.get('status') === 'succeeded') {
                                clearInterval(timer);

                                this.fetchContractById(contract.id).then((updatedContract) => resolve(updatedContract));
                            }
                        } catch (error: any) {
                            if (error.code === 101) {
                                clearInterval(timer);
                                resolve(null);
                            }
                        }
                    }, 3000);
                }
            } catch (e) {
                return reject(e);
            }
        });

        // return new Promise(async (resolve) => {
        //     const jobId = await Parse.Cloud.run('startContractRender', { contractId: contract.id });
        //
        //     if (jobId) {
        //         const timer = setInterval(async () => {
        //             try {
        //                 const result: Parse.Object = await Parse.Cloud.run('getJobStatus', { jobId });
        //
        //                 if (result.get('status') === 'succeeded') {
        //                     clearInterval(timer);
        //                     resolve(result);
        //                 }
        //             } catch (error: any) {
        //                 if (error.code === 101) {
        //                     clearInterval(timer);
        //                     resolve(null);
        //                 }
        //             }
        //         }, 15000);
        //     }
        // });
    }

    createEsignDocument(contract: Parse.Object) {
        return Parse.Cloud.run('createEsignDocument', { contractId: contract.id });
    }

    confirmContractReceipt(id: string) {
        return Parse.Cloud.run('confirmContractReceipt', { id: id })
    }

    generateContractPreviewHtml(contractId: string) {
        return Parse.Cloud.run('generateContractPreviewHtml', { contractId: contractId })
    }
}
